import * as THREE from 'three';
import { openGameArchives, normalizeAssetPath } from './dat.mjs';
import { loadHostedArchiveFiles } from './remote-archives.mjs';
import { parseLevelCatalog } from './levels.mjs';
import { parseEconomyCatalog, Progression, studMultiplier } from './progression.mjs';
import { parseGizmos } from './gizmos.mjs';
import { createPickupState } from './pickups.mjs';
import { createThings } from './things.mjs';
import { parseFlowGraph, deriveFreshPickupFlowOverrides, startFromSpline } from './scripts.mjs';
import { createSceneAnimationAdapter } from './scene-animation.mjs';
import { createInteractionState } from './interactions.mjs';
import { createObstacleState } from './obstacles.mjs';
import { MissionRuntime, parseAreaMessages } from './mission.mjs';
import { createRewardRuntime, deathStudLoss } from './rewards.mjs';
import { parseAI2, expandCreatureSpawns, parseEnemyScript } from './enemies.mjs';
import { createEnemyController } from './enemy-controller.mjs';
import { parseAudioConfig, createOriginalAudio } from './audio.mjs';
import { createProjectileSystem } from './projectiles.mjs';
import { evaluatePointOfInterest } from './ghg.mjs';
import { createSourceDeflector, attachThingsBlaster } from './deflection.mjs';
import { createRespawnController } from './respawn.mjs';
import { createLevelAIController } from './level-ai-controller.mjs';
import { createStoryParty, storyPartyStarts } from './party.mjs';
import { createPanelState, panelAbilityFlags } from './panels.mjs';
import { createBlowupState } from './blowups.mjs';

const decoder = new TextDecoder('utf-8', { fatal: true });
const arrayBuffer = bytes => bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength);

export function createLibrary({ scene, camera, playtest, loadScene, onError }) {
  const el = id => document.querySelector(`#${id}`), world = new THREE.Group(); world.scale.x = -1; scene.add(world);
  let assets = null, catalog = null, progression = null, selectedChapter = null, level = null, things = null, pickups = null, loading = false;
  let cache = new Map(), cacheBytes = 0, meshes = new Map(), lastSave = 0, lastHUD = 0;
  let sceneAnimation = null, sceneBindings = null;
  let ownedAnimationNames = new Set();
  let interactions = null, obstacles = null, mission = null, rewards = null, enemyController = null, audio = null;
  let campaign = false, activeInteraction = null, previousCenter = null, rewardMeshes = new Map(), drawnRewards = new Set();
  const runtimeEvents = [];
  let projectiles = null, boltMeshes = new Map();
  let respawn = null;
  let levelController = null, chapterMessages = new Map();
  let party = null;
  let hubMode = false, hubDoorLatch = false, hubEpisode = null;
  let panels = null, activePanel = null, panelMeshes = new Map();
  let blowups = null, blowupMeshes = new Map();
  const report = { state: 'empty', completeGame: false, scriptInitialization: false, missingModels: [], loadedLevels: [] };
  // The retail hub is an authored MAP level in GAME.DAT. It is deliberately
  // kept separate from the campaign catalog: MAP is not an ordinary chapter.
  const hubLevel = { id: 'map', file: 'map', dir: 'map', kind: 'hub', basePath: 'LEVELS/MAP/MAP', scenePath: 'LEVELS/MAP/MAP/MAP_PC.GSC' };
  async function bytes(path) {
    const key = normalizeAssetPath(path);
    if (cache.has(key)) { const value = cache.get(key); cache.delete(key); cache.set(key, value); return value; }
    const value = await assets.read(key);
    while (cacheBytes + value.length > 64 * 1024 * 1024 && cache.size) { const first = cache.keys().next().value; cacheBytes -= cache.get(first).length; cache.delete(first); }
    if (value.length <= 64 * 1024 * 1024) { cache.set(key, value); cacheBytes += value.length; }
    return value;
  }
  function file(path) {
    const normalized = normalizeAssetPath(path);
    return { name: normalized.split('/').at(-1), webkitRelativePath: normalized,
      async arrayBuffer() { return arrayBuffer(await bytes(normalized)); }, async text() { return decoder.decode(await bytes(normalized)); } };
  }
  function clearPickups() {
    panels?.release(activePanel?.id); panels = activePanel = null; panelMeshes.clear();
    blowups = null; blowupMeshes.clear();
    if (activeInteraction) interactions?.release(activeInteraction.id);
    activeInteraction = null; playtest.setInteraction(null); playtest.setGameHooks(null);
    enemyController?.dispose(); enemyController = null; audio?.stopAll();
    projectiles?.dispose(); projectiles = null; boltMeshes.clear();
    interactions = obstacles = rewards = null; previousCenter = null; rewardMeshes.clear(); drawnRewards.clear();
    respawn = null;
    levelController = null;
    party = null;
    world.clear(); meshes.clear(); pickups = null; el('cutscene-controls').hidden = true;
  }
  function pickupFor(ref) { return ref.type === 'gizmopickup' ? pickups?.pickups.find(p => p.name.toLowerCase() === ref.name) : null; }
  function canonicalRef(ref) {
    if (ref.type) return ref;
    const matches = [];
    for (const p of pickups?.pickups ?? []) if (p.name.toLowerCase() === ref.name) matches.push({ type: 'gizmopickup', name: ref.name });
    for (const r of interactions?.records ?? []) if (r.name.toLowerCase() === ref.name) matches.push({ type: r.kind === 'force' ? 'gizforce' : 'gizbuildit', name: ref.name });
    for (const r of obstacles?.records ?? []) if (r.name.toLowerCase() === ref.name) matches.push({ type: 'gizobstacle', name: ref.name });
    for (const r of panels?.records ?? []) if (r.name.toLowerCase() === ref.name) matches.push({ type: 'panel', name: ref.name });
    for (const r of blowups?.records ?? []) if (r.name.toLowerCase() === ref.name) matches.push({ type: 'blowup', name: ref.name });
    if (matches.length === 1) return matches[0];
    if (matches.length > 1) report.ambiguousGizmo = ref.name;
    return ref;
  }
  function worldActivate(ref, on) {
    ref = canonicalRef(ref);
    // Generic GizmoActivate reveals the object before its type callback.
    if (on) worldVisibility(ref, true);
    const pickup = pickupFor(ref);
    if (pickup) { pickups.activate(pickup.id, on); return true; }
    if (interactions?.resolve(ref)) return interactions.activate(ref, on);
    if (panels?.resolve(ref)) return panels.activate(ref, on);
    if (blowups?.resolve(ref)) return blowups.activate(ref, on);
    return obstacles?.activate(ref, on) ?? false;
  }
  function worldVisibility(ref, on) {
    ref = canonicalRef(ref);
    // Generic GizmoSetVisibility hides through deactivation first.
    if (!on) worldActivate(ref, false);
    const pickup = pickupFor(ref);
    if (pickup) { pickups.setVisibility(pickup.id, on); return true; }
    if (interactions?.resolve(ref)) return interactions.setVisibility(ref, on);
    if (panels?.resolve(ref)) return panels.setVisibility(ref, on);
    if (blowups?.resolve(ref)) return blowups.setVisibility(ref, on);
    return obstacles?.setVisibility(ref, on) ?? false;
  }
  function worldOutput(ref, output, ignore) {
    ref = canonicalRef(ref);
    const pickup = pickupFor(ref);
    if (pickup) return output === 0 ? pickup.collected : null;
    if (interactions?.resolve(ref)) return interactions.getOutput(ref, output, ignore);
    if (panels?.resolve(ref)) return panels.getOutput(ref, output, ignore);
    if (blowups?.resolve(ref)) return blowups.getOutput(ref, output, ignore);
    return obstacles?.getOutput(ref, output, ignore) ?? null;
  }
  function makeMission() {
    return new MissionRuntime({ chapterId: selectedChapter.id, mode: progression.snapshot().run?.mode ?? 'story', levelCatalog: catalog,
      worldBindings: { activate: worldActivate, setVisibility: worldVisibility, getOutput: worldOutput,
        activateReverse(ref, on, invisible) {
          const runtime = interactions?.resolve(ref) ? interactions : obstacles?.resolve(ref) ? obstacles : null;
          if (!runtime?.activateReverse(ref, on, invisible ? 7 : 3)) return false;
          runtime.activateReverse(ref, on, invisible ? 6 : 2);
          if (invisible) worldVisibility(ref, !on);
          return true;
        },
      },
      onBlocked(event) { report.missionBlocked = event; },
      onTransition(transition) { transitionTo(transition).catch(onError); },
      onTerminal(evidence) {
        report.terminal = evidence;
        const run = progression.snapshot().run;
        if (campaign && run?.chapter === evidence.chapterId && progression.catalog.chapters[run.chapter].completionRule) {
          progression.recordObjective('original-exit-chain');
          progression.completeChapter({ level: evidence.levelId, event: evidence.event }); progression.save();
          el('library-status').textContent = `${selectedChapter.file} chapter end reached. Supported progression saved.`;
        }
      },
    });
  }
  async function transitionTo(transition) {
    if (loading || !mission || transition !== mission.pendingTransition) return;
    const target = catalog.levels.get(transition.to); if (!target) throw new Error('Unknown original transition destination.');
    await openLevel({ transition, destination: target });
  }
  function configureHooks() {
    playtest.setGameHooks({
      update(dt) {
        for (const actor of [...playtest.partyActors, ...playtest.enemies]) {
          const p = actor.root.position, c = actor.config.values;
          actor.collisionPosition = [p.x, p.y + (c.miny + c.maxy) / 2, p.z];
        }
        respawn?.syncAlive({ position: playtest.player.root.position.toArray(), movementFacing: playtest.player.root.rotation.y });
        party?.tick(dt); levelController?.tick(dt); enemyController?.tick(dt); projectiles?.tick(dt);
        for (const event of respawn?.update(dt) ?? []) if (event.type === 'respawned') playtest.respawnAt(event.state.position, event.state.facing, event.state.protectionRemaining);
      },
      incomingBolts: () => projectiles?.activeBolts ?? [],
      onTag() { if (!activeInteraction && !activePanel && !playtest.player.dead) party?.next(); },
      getStrikeTargets: () => [...(obstacles?.hitTargets() ?? []), ...(blowups?.hitTargets({ supportingPlatformId: playtest.supportingPlatformId }) ?? [])],
      onWorldHit: target => target.kind === 'blowup' ? blowups.hit(target.id) : obstacles?.hit(target.id) ?? false,
      onActive(active) {
        if (active) audio?.resume().catch(error => { report.audioError = error.message; });
        else { if (activeInteraction) interactions?.release(activeInteraction.id); panels?.release(activePanel?.id); activePanel = null; activeInteraction = null; playtest.setInteraction(null); audio?.stopAll(); }
      },
      onStrike() { audio?.play('SaberMove01'); },
      onEnemyHit(actor) { enemyController?.notifyHit(actor); audio?.play('SaberSmack01'); },
      onPlayerHit(event) { if (activeInteraction) interactions.release(activeInteraction.id); panels?.release(activePanel?.id); activePanel = null; activeInteraction = null; playtest.setInteraction(null); audio?.play(playtest.player.config.values[event.killed ? 'sfx_die' : 'sfx_hurt']); },
      onPlayerDeath() {
        const state = progression.snapshot();
        if (state.run?.chapter === selectedChapter.id) {
          const loss = deathStudLoss(state.run.studs, { scoreMultiplierActive: state.enabledExtras.some(name => /^scorex/.test(name)) });
          progression.loseRunStuds(loss.lost);
          if (loss.dropped) rewards.spawnReward({ id: `${level.id}:death:${(respawn?.snapshot().deathCount ?? 0) + 1}`, kind: 'death', amount: loss.dropped, position: playtest.player.root.position.toArray() });
        }
        respawn?.kill({ reason: 2 }); playtest.player.root.visible = false;
      },
    });
  }
  function actorFiles(name) {
    const model = [...assets.entries.keys()].find(path => path.endsWith(`/${name.toLowerCase()}_pc.ghg`));
    if (!model) return [];
    const directory = model.slice(0, model.lastIndexOf('/') + 1);
    return [...assets.entries.keys()].filter(path => (path.startsWith(directory) && !path.slice(directory.length).includes('/') && /\.(an3|txt|ghg)$/.test(path)) || /^chars\/commonanims\/[^/]+\.an3$/.test(path)).map(file);
  }
  async function loadEnemies({ parsedScene, transition } = {}) {
    const aiDirectory = `${level.basePath.slice(0, level.basePath.lastIndexOf('/'))}/AI`;
    const aiPath = `${aiDirectory}/${level.file}.AI2`;
    if (!assets.has(aiPath)) { report.enemies = { state: 'no authored AI2' }; return; }
    const ai = parseAI2(await bytes(aiPath)), spawns = expandCreatureSpawns(ai, { difficulty: 1 });
    const roster = selectedChapter.settings?.characters.filter(character => character.role === 'player') ?? [];
    const scripts = new Map(), levelScripts = new Map(), globalScripts = new Map();
    const queue = [...new Set([...spawns.map(spawn => spawn.script), ...roster.map(member => member.id), 'level', 'party', 'jedi', 'blaster', 'noweapon', 'generalparty'])];
    while (queue.length) {
      const name = queue.shift(); if (scripts.has(name)) continue;
      const local = `${aiDirectory}/${name}.SCP`, global = `SCRIPTS/${name}.SCP`;
      for (const [path, destination] of [[local, levelScripts], [global, globalScripts]]) if (assets.has(path)) {
        const parsed = parseEnemyScript(decoder.decode(await bytes(path)), { name }); destination.set(name, parsed);
        for (const state of parsed.states.values()) for (const reference of state.references) if (!scripts.has(reference.script)) queue.push(reference.script);
      }
      scripts.set(name, levelScripts.get(name) ?? globalScripts.get(name) ?? null);
    }
    const matchingDoor = transition?.exitName && mission.config.doors.find(door => door.name === transition.exitName);
    const starts = parsedScene.splines.get(matchingDoor?.spline ?? 'start')?.points;
    if (roster.length && starts) {
      const initial = storyPartyStarts(roster, starts, { doorEntry: !!matchingDoor });
      const members = [{ name: roster[0].id, actor: playtest.player }];
      for (const descriptor of initial.slice(1)) {
        const actor = await playtest.addPartyMember(descriptor.name, actorFiles(descriptor.name), { ...descriptor, thingsBuffer: arrayBuffer(await bytes('STUFF/THINGS_PC.GSC')) });
        if (actor) members.push({ name: descriptor.name, actor });
      }
      party = createStoryParty({ members, ai, levelScripts, globalScripts, messages: mission.messages, missionMode: false,
        cutsceneLocked: () => !!activeInteraction || !!activePanel || playtest.controls.attacking,
        obstacleAtEnd: name => obstacles.getOutput(name, 0, true),
        getFloor: (x, y, z) => playtest.floorAt(x, y + .25, z, .6)?.point.y ?? null,
        getLineOfSight(actor, target) { const p = actor.root.position; return playtest.hasLineOfSight([p.x, p.y + .2, p.z], [target.position[0], target.position[1] + .2, target.position[2]], 0); },
        onTransfer({ target }) { playtest.controlActor(target); previousCenter = null; },
      });
      report.party = { members: members.map(member => member.name), runtime: party.report };
    }
    const actors = [], failed = [];
    for (const spawn of spawns.filter(spawn => ['active', 'ready'].includes(spawn.runtimeState))) {
      try {
        const actor = await playtest.addEnemy(spawn.character, actorFiles(spawn.character), { id: spawn.id, position: spawn.position, heading: spawn.heading, ai: spawn });
        if (actor) {
          if (actor.config.values.blaster && actor.config.values.variant === 'battledroid') actor.weaponVisual = await attachThingsBlaster(actor, things.parsed);
          actors.push({ spawn, actor });
        }
      } catch (error) { failed.push({ id: spawn.id, character: spawn.character, error: error.message }); }
    }
    enemyController = createEnemyController({ ai, actors, scripts, difficulty: 1, player: () => playtest.player, messages: mission.messages,
      forceComplete: name => interactions.getOutput(name, 6, true), obstacleAtEnd: name => obstacles.getOutput(name, 0, true),
      getFloor: (x, y, z) => playtest.floorAt(x, y + 0.25, z, 0.6)?.point.y ?? null,
      getLineOfSight(actor, target) {
        const cfg = actor.config.values, from = [actor.root.position.x, actor.root.position.y + (cfg.miny + cfg.maxy) / 2, actor.root.position.z];
        const to = target.movementProbe ? [target.position[0], target.position[1] + (cfg.miny + cfg.maxy) / 2, target.position[2]] : [target.root.position.x, target.root.position.y + (target.config.values.miny + target.config.values.maxy) / 2, target.root.position.z];
        return playtest.hasLineOfSight(from, to, target.movementProbe ? 0 : target.config.values.radius);
      },
      onFire(actor, target, request) { projectiles?.requestFire(actor, target, request); },
    });
    if (scripts.get('level')) {
      levelController = createLevelAIController({ ai, script: scripts.get('level'), scripts, messages: mission.messages,
        players: () => playtest.partyActors.filter(actor => actor.playerControlled).map(actor => ({ name: actor.name, position: actor.root.position.toArray() })),
        forceComplete: name => interactions.getOutput(name, 6, true),
        getObjectState(name) {
          for (const runtime of [interactions, obstacles]) for (const record of runtime.records) {
            const piece = record.pieces.find(piece => piece.name.toLowerCase() === name.toLowerCase());
            if (piece) return { visible: record.visible, frame: piece.frame, endFrame: piece.endFrame };
          }
          const binding = sceneAnimation.resolve(name), object = binding && sceneAnimation.records.find(record => record.name.toLowerCase() === name.toLowerCase());
          return object ? { visible: sceneBindings.getObject(name)?.visible === true, frame: binding.currentFrame, endFrame: binding.endFrame } : null;
        },
        onPlaySfx: request => { if (request.name) audio?.play(request.name); },
      });
      report.levelAI = levelController.report;
    }
    report.enemies = { authored: spawns.length, loaded: actors.length, failed, controller: enemyController.report };
  }
  function configureProjectiles() {
    const deflector = createSourceDeflector({ getContext(actor) {
      if (actor !== playtest.player) return { name: 'none', playerControlled: false, jedi: false };
      return { name: actor.blockController.state.name !== 'none' ? actor.blockController.state.name : actor.combo.state.active ? 'combo' : 'none', playerControlled: true, playerIndex: 0, jedi: actor.config.values.jedi === true, jumpFlags: 0 };
    } });
    projectiles = createProjectileSystem({
      getShootOrigin(actor, locator) {
        if (actor.aiFireClip) actor.pose(actor.aiFireClip, actor.aiFrame);
        const poi = evaluatePointOfInterest(actor.parsed, locator, actor.matrices); if (!poi) return null;
        actor.model.updateMatrix(); actor.root.updateMatrix();
        return new THREE.Vector3(poi[12], poi[13], poi[14]).applyMatrix4(actor.model.matrix).applyMatrix4(actor.root.matrix).toArray();
      },
      getAimPosition: (actor, target) => [target.root.position.x, target.root.position.y + (target.config.values.miny + target.config.values.maxy) / 2, target.root.position.z],
      terrainSweep(start, end, radius) {
        const delta = end.map((v, i) => v - start[i]), hit = playtest.sweepParticle({ position: start, velocity: delta, duration: 1, radius });
        return hit ? { fraction: hit.time } : null;
      },
      getTargets: () => [playtest.player, ...playtest.enemies],
      getCollision: (target, bolt) => ({ position: [target.root.position.x, target.root.position.y + (target.config.values.miny + target.config.values.maxy) / 2, target.root.position.z], radius: target.config.values.radius, halfHeight: (target.config.values.maxy - target.config.values.miny) / 2, eligible: !target.dead && (target === playtest.player ? bolt.owner !== target : bolt.owner === playtest.player && target.eligibleDamage !== false) }),
      interceptCharacterHit: deflector.interceptCharacterHit,
      onDamage: (target, amount) => target === playtest.player ? playtest.damagePlayer(amount, { invincible: progression.snapshot().enabledExtras.includes('invincibility') }) : playtest.damageEnemy(target, amount),
      onSpawn(bolt) {
        if (!things.has(bolt.model)) { report.missingBoltModel = bolt.model; return; }
        const model = things.create(bolt.model); world.add(model); boltMeshes.set(bolt.id, model); audio?.play('Blaster');
      },
      onEnd(bolt) { boltMeshes.get(bolt.id)?.removeFromParent(); boltMeshes.delete(bolt.id); },
    });
    report.projectiles = projectiles.report;
  }
  function options(select, values, label, value) {
    select.replaceChildren();
    for (const item of values) { const option = document.createElement('option'); option.value = value(item); option.textContent = label(item); select.append(option); }
  }
  function populateChapters() {
    const episode = catalog.episodes[Number(el('episode').value) - 1];
    options(el('chapter'), episode.chapters, chapter => chapter.file.replaceAll('_', ' '), chapter => chapter.id);
    populateLevels();
  }
  function populateLevels() {
    clearPickups(); playtest.setActive(false); mission = null; campaign = false;
    selectedChapter = catalog.areas.get(el('chapter').value);
    const available = selectedChapter.levels.filter(level => !level.unresolved && assets.has(level.scenePath));
    options(el('level'), available, level => `${level.file} · ${level.kind}`, level => level.id);
    if (available.some(level => level.id === selectedChapter.firstGameplayLevelId)) el('level').value = selectedChapter.firstGameplayLevelId;
    el('open-level').disabled = !available.length; updateHUD();
  }
  function hubDoorAt(position) {
    if (!hubMode || !obstacles) return null;
    const nearest = obstacles.records.filter(record => /^de[1-6](?:_[1-6]|_back)?$/i.test(record.name)).map(record => ({ record,
      distance: Math.hypot(record.position[0] - position.x, record.position[2] - position.z) })).sort((a, b) => a.distance - b.distance)[0];
    if (!nearest || nearest.distance > 1.1) return null;
    const episodeDoor = /^de([1-6])$/i.exec(nearest.record.name);
    if (episodeDoor) {
      const episode = Number(episodeDoor[1]), unlocked = catalog.episodes[episode - 1].chapters.some(chapter => progression.snapshot().unlockedChapters.includes(chapter.id));
      return { kind: 'episode', episode, unlocked, name: nearest.record.name, distance: nearest.distance };
    }
    const backDoor = /^de([1-6])_back$/i.exec(nearest.record.name);
    if (backDoor) return { kind: 'back', episode: Number(backDoor[1]), name: nearest.record.name, distance: nearest.distance };
    const match = /^de([1-6])_([1-6])$/i.exec(nearest.record.name);
    const chapter = catalog.episodes[Number(match[1]) - 1]?.chapters[Number(match[2]) - 1];
    return chapter ? { kind: 'chapter', episode: Number(match[1]), chapter, unlocked: hubEpisode === Number(match[1]) && progression.snapshot().unlockedChapters.includes(chapter.id), name: nearest.record.name, distance: nearest.distance } : null;
  }
  async function enterHubDoor(door) {
    if (loading || !door || door.kind !== 'chapter' || !door.unlocked) return;
    const run = progression.snapshot().run;
    if (run && run.chapter !== door.chapter.id) return;
    selectedChapter = door.chapter;
    el('episode').value = String(catalog.episodes.find(episode => episode.chapters.includes(door.chapter)).number);
    populateChapters(); el('chapter').value = door.chapter.id; populateLevels();
    if (!run) { progression.startRun(door.chapter.id, 'story'); progression.save(); }
    el('level').value = door.chapter.firstGameplayLevelId;
    await openLevel({ storySession: true });
  }
  function activateHubDoor(door) {
    if (!door) return;
    if (door.kind === 'episode' && door.unlocked) { hubEpisode = door.episode; return; }
    if (door.kind === 'back') { hubEpisode = null; return; }
    enterHubDoor(door).catch(onError);
  }
  async function openHub() {
    if (!assets || !catalog) return;
    // A fresh retail save enters the cantina as Qui-Gon. The source game later
    // replaces this through its hub player-list and save-state logic.
    selectedChapter = catalog.areas.get('negotiations');
    await openLevel({ destination: hubLevel, hub: true });
  }
  function updateHUD() {
    if (!progression) return;
    const saved = progression.snapshot(), run = saved.run;
    el('economy-status').textContent = `Bank ${saved.bank.toLocaleString()} · Run ${(run?.studs ?? 0).toLocaleString()} studs\n${saved.goldBricks.length} gold bricks · ×${studMultiplier(saved.enabledExtras)}\n${run ? `${run.chapter} · ${run.mode} · True Jedi ${run.trueJedi ? 'reached' : 'pending'}` : 'No active chapter ledger'}`;
    el('start-run').disabled = loading || !selectedChapter || !saved.unlockedChapters.includes(selectedChapter.id);
    el('bank-run').disabled = loading || !run;
    el('save-game').disabled = loading;
    el('buy-item').disabled = !el('shop-item').value || !progression.canPurchase(el('shop-item').value);
    report.progress = saved;
  }
  function populateShop() {
    const items = [...Object.values(progression.catalog.characters).filter(c => c.price !== null).map(c => ({ id: `character:${c.id}`, price: c.price })),
      ...Object.values(progression.catalog.extras).map(e => ({ id: `extra:${e.id}`, price: e.price }))];
    options(el('shop-item'), items, item => `${item.id.replace(':', ' · ')} — ${item.price.toLocaleString()}`, item => item.id); updateHUD();
  }
  function buildPickups(gizmos, graph = null) {
    clearPickups(); if (!things) return;
    gizmos ??= { pickups: [], summary: { pickups: 0 }, unsupportedSections: [] };
    const saved = progression.snapshot(), run = saved.run, ledger = run?.chapter === selectedChapter.id ? run : null;
    const prefix = `${level.id}:`, minikits = saved.chapters[selectedChapter.id].minikits;
    const runMinikits = (ledger?.minikits ?? []).filter(id => id.startsWith(prefix)).map(id => id.slice(prefix.length));
    const collected = new Set(ledger?.pickupIds ?? []);
    const flowReset = graph ? deriveFreshPickupFlowOverrides(graph, gizmos, { freeplay: ledger?.mode === 'freeplay' }) : null;
    pickups = createPickupState(gizmos, { mode: ledger?.mode ?? 'story', gizmoFlags: flowReset?.overrides, progress: {
      collectedIds: gizmos.pickups.filter(pickup => collected.has(`${level.id}:${pickup.id}`.toLowerCase()) ||
        (pickup.typeCode === 'm' && runMinikits.includes(pickup.name.toLowerCase()))).map(pickup => pickup.id),
      minikitNames: minikits.filter(id => id.startsWith(prefix)).map(id => id.slice(prefix.length)),
      redBrickCollected: saved.chapters[selectedChapter.id].redBrick || !!ledger?.redBrick,
    } });
    report.missingModels = [];
    for (const pickup of pickups.pickups) {
      if (!pickup.modelName) continue;
      try {
        const model = things.create(pickup.modelName);
        model.traverse(mesh => { if (mesh.isMesh) mesh.onAfterRender = () => pickups?.markDrawn(pickup.id, true); });
        world.add(model); meshes.set(pickup.id, model);
      } catch (error) { report.missingModels.push({ id: pickup.id, model: pickup.modelName, reason: error.message }); }
    }
    report.pickups = gizmos.summary; report.unsupportedGizmoSections = gizmos.unsupportedSections;
    report.flowReset = flowReset ? { phase: flowReset.phase, resolved: flowReset.overrides.size, unresolved: flowReset.unresolved, requiresRuntime: flowReset.requiresRuntime } : null;
  }
  async function openLevel({ transition = null, destination = null, storySession = false, hub = false } = {}) {
    if (loading || !assets) return; loading = true; el('open-level').disabled = true; el('start-run').disabled = true;
    clearPickups(); playtest.clear(); report.state = 'loading'; el('library-status').textContent = 'Decoding original level assets…';
    try {
      level = destination ?? catalog.levels.get(el('level').value); hubMode = hub; if (hub) hubEpisode = null;
      if (!level) throw new Error('Unknown original level.');
      if (!transition) { mission = hub ? null : makeMission(); campaign = !hub && storySession; }
      const textPath = `${level.basePath}.TXT`, configText = assets.has(textPath) ? decoder.decode(await bytes(textPath)) : '';
      const gitPath = `${level.basePath}.GIT`, gitText = assets.has(gitPath) ? decoder.decode(await bytes(gitPath)) : '';
      if (transition && ['outro', 'status'].includes(level.kind)) {
        const entered = mission.commitTransition(transition.token, { id: level.id, configText, gitText, scene: null, messages: new Map([...(chapterMessages.get(selectedChapter.id) ?? []), ...parseAreaMessages(configText)]) });
        report.level = level.id; report.state = level.kind === 'outro' ? 'cutscene' : 'status';
        if (level.kind === 'outro') {
          const reference = entered.cutsceneReferences[0]; if (!reference) throw new Error('Original outro has no cinematic reference.');
          const base = `CUT/${reference.replaceAll('\\', '/')}`;
          const descriptor = mission.loadCutscene({ reference, configText: decoder.decode(await bytes(`${base}.TXT`)), bytes: await bytes(`${base}.CU2`) });
          el('cutscene-status').textContent = `Original ${reference} reached. Cinematic rendering is unfinished; the original skip action is available.`;
          el('skip-cutscene').disabled = descriptor.unskippableInStory; el('cutscene-controls').hidden = false;
        }
        return;
      }
      const parsed = await loadScene(file(level.scenePath));
      if (!parsed) throw new Error('Original level scene could not be loaded.');
      const characterName = hub ? 'quigonjinn' : selectedChapter.settings?.characters.find(character => character.role === 'player')?.id;
      if (!characterName) throw new Error('Original story roster has no controlled character.');
      const files = actorFiles(characterName);
      files.push(file('STUFF/THINGS_PC.GSC'));
      const terrain = `${level.basePath}.TER`, gizmo = `${level.basePath}.GIZ`;
      if (assets.has(terrain)) files.push(file(terrain));
      // Original script-owned enemy placement is not replaced with test spawns.
      await playtest.load(files, { testEncounter: false, characterName });
      if (playtest.report.state !== 'ready') throw new Error('Character/terrain adapter failed for this level.');
      const start = parsed.splines.get('start');
      if (start && start.points.length >= 2) {
        const original = startFromSpline(start.points, 0);
        playtest.setSpawn([original.position.x, original.position.y, original.position.z], original.heading);
        report.originalSpawn = original;
      } else report.originalSpawn = null;
      const gizmos = assets.has(gizmo) ? parseGizmos(arrayBuffer(await bytes(gizmo))) : null;
      const flowPath = `${level.basePath}.GIT`, graph = assets.has(flowPath) ? parseFlowGraph(decoder.decode(await bytes(flowPath))) : null;
      sceneAnimation = createSceneAnimationAdapter(parsed, parsed.bindings);
      sceneBindings = parsed.bindings;
      playtest.setDynamicTerrainBindings(index => {
        const binding = sceneAnimation.terrainBindings.get(index), object = binding && parsed.bindings.getObject(binding.name);
        return object ? () => ({ matrix: object.matrix, active: object.visible }) : null;
      });
      buildPickups(gizmos, graph);
      interactions = createInteractionState(gizmos ?? { forces: [], buildits: [] }, { animationAdapter: sceneAnimation });
      const platformNames = new Map();
      for (const [index, binding] of sceneAnimation.terrainBindings) platformNames.set(binding.name.toLowerCase(), index);
      // The standing-contact API uses the original TER group index consistently.
      obstacles = createObstacleState(gizmos ?? { obstacles: [] }, { animationAdapter: sceneAnimation, platformForObject: name => {
        const serializedIndex = platformNames.get(name.toLowerCase());
        return playtest.terrainGroupForPlatform(serializedIndex);
      } });
      ownedAnimationNames = new Set([...interactions.records, ...obstacles.records].flatMap(record => record.pieces.map(piece => piece.name)));
      panels = createPanelState(gizmos ?? { panels: [] }, {
        floorProbe: (position, probeHeight) => playtest.floorAt(position[0], position[1] + probeHeight, position[2], probeHeight + .5)?.point.y ?? null,
        animationDuration(actor, action) {
          const animation = actor.character.config.actions.get(action); if (!animation) return 0;
          const clip = actor.character.animations.get(animation.clip); return clip ? (clip.endFrame - 1) / animation.playbackRate : null;
        },
      });
      blowups = createBlowupState(gizmos ?? {}, { animationAdapter: sceneAnimation, platformForObject: name => playtest.terrainGroupForPlatform(platformNames.get(name.toLowerCase())) });
      for (const type of gizmos?.blowupTypes ?? []) {
        ownedAnimationNames.add(type.modelName);
        sceneBindings.setVisible(type.modelName, false);
      }
      for (const draw of blowups.getDrawStates()) {
        if (!draw.matrix) continue;
        const source = sceneBindings.getObject(draw.modelName); if (!source?.matrix) continue;
        const group = new THREE.Group(); group.matrixAutoUpdate = false; group.matrix.fromArray(draw.matrix);
        const inverse = new THREE.Matrix4().fromArray(source.originalMatrix).invert();
        for (const mesh of source.meshes) {
          const clone = new THREE.Mesh(mesh.geometry, mesh.material); clone.name = mesh.name; clone.matrixAutoUpdate = false;
          clone.matrix.copy(inverse).multiply(mesh.matrix);
          group.add(clone);
        }
        group.visible = draw.visible; world.add(group); blowupMeshes.set(draw.id, group);
      }
      rewards = createRewardRuntime({ modelAvailable: name => things.has(name) });
      const input = { id: level.id, configText, gitText, scene: parsed, messages: new Map([...(chapterMessages.get(selectedChapter.id) ?? []), ...parseAreaMessages(configText)]) };
      const entered = hub ? null : transition ? mission.commitTransition(transition.token, input) : mission.enterLevel(input);
      if (entered?.entry) playtest.setSpawn(Object.values(entered.entry.position), entered.entry.heading);
      respawn = createRespawnController({ initialPosition: playtest.player.root.position.toArray(), initialFacing: playtest.player.root.rotation.y, maxHealth: playtest.player.health.maximum });
      configureProjectiles(); if (!hub) await loadEnemies({ parsedScene: parsed, transition }); configureHooks();
      report.state = 'ready'; report.level = level.id; report.chapter = hub ? null : selectedChapter.id; report.hub = hub;
      report.missionFlow = entered?.flow ?? null; report.interactionCount = interactions.records.length; report.obstacleCount = obstacles.records.length;
      if (!report.loadedLevels.includes(level.id)) report.loadedLevels.push(level.id);
      el('library-status').textContent = hub ? 'Original Mos Eisley Cantina loaded from GAME.DAT. Walk to an unlocked chapter door and hold K.' : `${level.file} loaded from original DAT\nOriginal puzzle animations, obstacle states, AI scripts and transitions are connected. Full-game parity remains incomplete.`;
      if (hub) {
        el('retail-launch').hidden = true;
        el('retail-hud').textContent = 'MOS EISLEY CANTINA · Walk to the Episode I door';
        el('help').textContent = 'WASD move · Space jump · J attack · Hold K at a hub door';
      } else el('retail-hud').textContent = `EPISODE ${catalog.episodes.find(episode => episode.chapters.includes(selectedChapter))?.number ?? ''} · ${selectedChapter.file.replaceAll('_', ' ')}`;
      updateHUD();
      if (hub || storySession || transition) playtest.setActive(true);
    } catch (error) { clearPickups(); report.state = 'error'; onError(error); el('library-status').textContent = error.message; }
    finally { loading = false; el('open-level').disabled = false; updateHUD(); }
  }
  async function load(files) {
    if (loading) return; loading = true; let bootHub = false; report.state = 'indexing'; el('steam-folder').disabled = true; el('library-status').textContent = 'Indexing original archives locally…';
    try {
      clearPickups(); playtest.clear(); audio?.dispose(); audio = null; mission = null; campaign = false; things?.dispose(); things = null; cache = new Map(); cacheBytes = 0;
      assets = await openGameArchives(files);
      const texts = {};
      for (const name of ['AREAS', 'EPISODES', 'LEVELS', 'MISSIONS']) texts[name.toLowerCase()] = await assets.text(`LEVELS/${name}.TXT`);
      const preliminary = parseLevelCatalog(texts), areaFiles = {}, areaTexts = {};
      for (const area of preliminary.areas.values()) if (assets.has(area.metadataPath)) { areaFiles[area.metadataPath] = await assets.text(area.metadataPath); areaTexts[area.id] = areaFiles[area.metadataPath]; }
      catalog = parseLevelCatalog({ ...texts, areaFiles });
      chapterMessages = new Map(Object.entries(areaTexts).map(([id, text]) => [id, parseAreaMessages(text)]));
      const status = catalog.areas.get('negotiations')?.levels.find(level => level.kind === 'status');
      const completionRules = status ? { negotiations: { level: status.id, event: 'original-outro-status', objectiveIds: ['original-exit-chain'] } } : {};
      const economy = parseEconomyCatalog({ areasText: texts.areas, episodesText: texts.episodes, areaTexts, completionRules, collectionText: await assets.text('CHARS/COLLECTION.TXT') });
      progression = new Progression(economy, { storage: localStorage }); progression.load();
      things = createThings(arrayBuffer(await bytes('STUFF/THINGS_PC.GSC')));
      if (assets.has('AUDIO/AUDIO.CFG')) { audio = createOriginalAudio(parseAudioConfig(await assets.text('AUDIO/AUDIO.CFG')), bytes, { onError: error => { report.audioError = error.message; } }); report.audio = audio.report; }
      options(el('episode'), catalog.episodes, episode => `Episode ${episode.number}`, episode => String(episode.number));
      el('episode').disabled = el('chapter').disabled = el('level').disabled = false;
      populateChapters(); populateShop();
      report.state = 'indexed'; report.catalog = catalog.counters; report.archives = assets.archives.length; report.assets = assets.entries.size;
      el('library-status').textContent = `${assets.archives.length} original archives · ${assets.entries.size.toLocaleString()} assets\nLoading the original Mos Eisley Cantina…`;
      bootHub = true;
    } catch (error) {
      clearPickups(); playtest.clear(); things?.dispose(); things = null;
      assets = null; catalog = null; progression = null; selectedChapter = null; level = null;
      for (const name of ['episode', 'chapter', 'level', 'open-level', 'start-run', 'bank-run', 'save-game', 'buy-item']) el(name).disabled = true;
      el('economy-status').textContent = 'Economy not loaded.';
      report.state = 'error'; onError(error); el('library-status').textContent = error.message;
    }
    finally { loading = false; el('steam-folder').disabled = false; updateHUD(); }
    if (bootHub) await openHub();
  }
  async function loadHosted() { await load(await loadHostedArchiveFiles()); }
  function update(dt) {
    if (!pickups || report.state !== 'ready' || !playtest.player) return;
    const player = playtest.player, p = player.root.position, config = player.config.values;
    const actor = { collisionPosition: { x: p.x, y: p.y + (config.miny + config.maxy) / 2, z: p.z },
      collisionMin: { x: p.x - config.radius, y: p.y + config.miny, z: p.z - config.radius },
      collisionMax: { x: p.x + config.radius, y: p.y + config.maxy, z: p.z + config.radius }, collisionRadius: config.radius, yRadius: (config.maxy - config.miny) / 2 };
    const saved = progression.snapshot(), ledger = saved.run?.chapter === selectedChapter.id ? saved.run : null;
    actor.coinMagnet = saved.enabledExtras.includes('coinmagnet');
    actor.playerActive = playtest.report.active && !player.dead; actor.playerControlled = true; actor.playerIndex = 0; actor.collectDisabled = !ledger;
    try {
      if (playtest.report.active) {
        report.ambientAnimation = sceneAnimation.updateAmbient(dt, { ownedNames: ownedAnimationNames });
        const forceActor = { collisionPosition: actor.collisionPosition, canForce: config.jedi === true, canBuild: player.config.actions.has('build'), darkForce: config.baddie === true,
          facing: [Math.sin(player.root.rotation.y), 0, Math.cos(player.root.rotation.y)], lowerY: p.y + config.miny, upperY: p.y + config.maxy,
          fastBuild: saved.enabledExtras.includes('fastbuild'), fastForce: saved.enabledExtras.includes('fastforce') };
        let candidate = null;
        if (!player.dead && !activePanel && playtest.controls.grounded && !playtest.controls.attacking) {
          candidate = interactions.records.filter(record => interactions.canUse(record.id, forceActor)).sort((a, b) =>
            a.position.reduce((sum, v, i) => sum + (v - [p.x, p.y, p.z][i]) ** 2, 0) - b.position.reduce((sum, v, i) => sum + (v - [p.x, p.y, p.z][i]) ** 2, 0))[0] ?? null;
        }
        if (!playtest.controls.interacting || !candidate || candidate.id !== activeInteraction?.id) {
          if (activeInteraction) { interactions.release(activeInteraction.id); audio?.stop(`interaction:${activeInteraction.id}`); }
          activeInteraction = null; if (!activePanel) playtest.setInteraction(null);
        }
        if (playtest.controls.interacting && candidate) {
          interactions.use(candidate.id, forceActor); activeInteraction = candidate;
          playtest.setInteraction({ id: candidate.id, kind: candidate.kind, heading: Math.atan2(candidate.position[0] - p.x, candidate.position[2] - p.z) });
        }
        interactions.update(dt);
        const panelActor = activePanel?.actor ?? { character: player, position: p, modelFlags: panelAbilityFlags(player.config), planarRadius: config.radius, context: -1, protocolParts: 4 };
        panelActor.health = player.health.current; panelActor.alive = !player.dead; panelActor.playerActive = playtest.report.active;
        panelActor.panelAnimationPlaying = activePanel ? player.currentClip === player.config.actions.get(activePanel.action)?.clip : false;
        const panelCandidate = !activeInteraction && !activePanel && playtest.controls.grounded && !playtest.controls.attacking ? panels.nearest(panelActor)?.record : null;
        if (panelCandidate && playtest.controls.interacting && panels.use(panelCandidate.id, panelActor)) activePanel = panelCandidate;
        panels.update(dt);
        for (const event of panels.drainEvents()) {
          if (event.type === 'started' || event.type === 'pose') playtest.setInteraction({ id: event.id, kind: 'panel', action: event.action, heading: event.heading, targetVelocity: event.targetVelocity });
          if (event.type === 'complete' || event.type === 'released') { activePanel = null; playtest.setInteraction(null); }
          if (event.type !== 'pose' && event.type !== 'visibility') {
            const { actor: _actor, ...safe } = event; runtimeEvents.push({ ...safe, level: level.id }); if (runtimeEvents.length > 100) runtimeEvents.shift();
          }
        }
        const body = { lowerPosition: [p.x, p.y + config.miny, p.z], collisionPosition: [actor.collisionPosition.x, actor.collisionPosition.y, actor.collisionPosition.z],
          isPlayer: true, playerIndex: 0, active: !player.dead, disabled: player.dead, supportingPlatformId: playtest.supportingPlatformId };
        obstacles.update(dt, { actors: [body], cameraPosition: [-camera.position.x, camera.position.y, camera.position.z] });
        blowups.update(dt);
        for (const event of [...interactions.drainEvents(), ...obstacles.drainEvents()]) {
          runtimeEvents.push({ ...event, level: level.id }); if (runtimeEvents.length > 100) runtimeEvents.shift();
          if (event.type === 'reward') rewards.spawnReward({ ...event, id: `${level.id}:${event.id}` });
          if (event.type === 'blowup') blowups.emitType(event.blowupName, event.position);
          if (event.type === 'started') audio?.play(event.kind === 'build' ? 'LegoForm' : 'ForceMove', { id: `interaction:${event.id}` });
          if (event.type === 'piecePlaced') audio?.play('LegoPieceAdded');
          if (event.type === 'complete') { audio?.stop(`interaction:${event.id}`); audio?.play('LegoClicks'); }
        }
        for (const event of blowups.drainEvents()) {
          runtimeEvents.push({ ...event, level: level.id }); if (runtimeEvents.length > 100) runtimeEvents.shift();
          if (event.type === 'reward') rewards.spawnReward({ ...event, id: `${level.id}:${event.id}` });
        }
        report.missionFlow = mission?.tick(dt) ?? null;
        const hubDoor = hubDoorAt(p);
        report.hubDoor = hubDoor ? { name: hubDoor.name, chapter: hubDoor.chapter.id } : null;
        if (!playtest.controls.interacting) hubDoorLatch = false;
        if (hubDoor && playtest.controls.interacting && !hubDoorLatch) {
          hubDoorLatch = true; activateHubDoor(hubDoor);
        }
        if (hubMode) {
          const prompt = hubDoor?.kind === 'episode' ? (hubDoor.unlocked ? `HOLD K · ENTER EPISODE ${hubDoor.episode}` : `EPISODE ${hubDoor.episode} LOCKED`) :
            hubDoor?.kind === 'back' ? 'HOLD K · RETURN TO CANTINA' : hubDoor?.kind === 'chapter' ? (hubDoor.unlocked ? `HOLD K · ENTER ${hubDoor.chapter.file.replaceAll('_', ' ').toUpperCase()}` : 'CHAPTER LOCKED') :
              (hubEpisode ? `EPISODE ${hubEpisode} · Find an unlocked chapter door` : 'MOS EISLEY CANTINA · Find an unlocked episode door');
          el('retail-hud').textContent = prompt;
        }
        report.interaction = activeInteraction ? { id: activeInteraction.id, name: activeInteraction.name, kind: activeInteraction.kind } : null;
        el('mission-status').textContent = player.dead ? 'Rebuilding character…' : hubDoor ? `Hold K · Enter ${hubDoor.chapter.file.replaceAll('_', ' ')}` : activePanel ? `Using panel · ${activePanel.name}` : candidate ? `Hold K · ${candidate.kind === 'force' ? 'Force' : 'Build'} · ${candidate.name}` : panelCandidate && panels.canUse(panelCandidate.id, panelActor) ? `Press K · Use panel · ${panelCandidate.name}` : `${player.name} · U switch · ${level.file}`;
        for (const event of rewards.update(dt, { actor, floor: query => playtest.sweepParticle(query), drawn: id => drawnRewards.has(id) })) {
          if (ledger) { progression.collectStud(event.id, { s: 'silver', g: 'gold', b: 'blue', p: 'purple' }[event.typeCode]); audio?.play(event.typeCode === 'b' ? 'PickupCoinB' : 'PickupCoin'); }
        }
      }
      drawnRewards.clear();
      for (const draw of blowups.getDrawStates()) { const model = blowupMeshes.get(draw.id); if (model) model.visible = draw.visible; }
      for (const draw of panels.drawStates()) {
        let group = panelMeshes.get(draw.id);
        const signature = draw.modelNames.join(':');
        if (!group || group.userData.signature !== signature) {
          group?.removeFromParent(); group = new THREE.Group(); group.userData.signature = signature;
          for (const name of draw.modelNames) if (things.has(name)) group.add(things.create(name));
          panelMeshes.set(draw.id, group); world.add(group);
        }
        group.position.fromArray(draw.position); group.rotation.y = draw.heading; group.visible = draw.visible;
      }
      const activeRewards = new Set();
      for (const draw of rewards.drawStates()) {
        activeRewards.add(draw.id);
        let model = rewardMeshes.get(draw.id);
        if (!model) {
          model = things.create(draw.modelName); world.add(model); rewardMeshes.set(draw.id, model);
          model.traverse(mesh => { if (mesh.isMesh) mesh.onAfterRender = () => drawnRewards.add(draw.id); });
        }
        model.position.fromArray(draw.position); model.scale.setScalar(draw.scale); model.rotation.y = draw.rotation; model.visible = draw.visible;
      }
      for (const [id, model] of rewardMeshes) if (!activeRewards.has(id)) { model.removeFromParent(); rewardMeshes.delete(id); }
      for (const bolt of projectiles.activeBolts) {
        const model = boltMeshes.get(bolt.id); if (!model) continue;
        model.position.fromArray(bolt.position); model.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(...bolt.direction)); model.scale.fromArray(bolt.visualScale);
      }
    } catch (error) { playtest.setActive(false); report.state = 'error'; onError(error); return; }
    pickups.update(dt);
    for (const pickup of pickups.pickups) {
      // This development ledger uses only the implemented fresh-reset subset.
      // Script-required activation remains disabled until a real event enables it.
      if (playtest.report.active && !player.dead && ledger && !(pickup.configFlags & 4) && ['s', 'g', 'b', 'p', 'm', 'r', 'h'].includes(pickup.effectiveTypeCode) &&
        (pickup.effectiveTypeCode !== 'h' || player.health.current < player.health.maximum) &&
        (pickup.effectiveTypeCode !== 'm' || /^[a-z0-9_./-]{1,8}$/i.test(pickup.name))) {
        const event = pickups.collect(pickup.id, actor);
        if (event) {
          if (['s', 'g', 'b', 'p'].includes(event.effectiveTypeCode)) progression.collectStud(`${level.id}:${pickup.id}`, { s: 'silver', g: 'gold', b: 'blue', p: 'purple' }[event.effectiveTypeCode]);
          else if (event.effectiveTypeCode === 'm') progression.collectMinikit(level.id, event.name);
          else if (event.effectiveTypeCode === 'r') progression.collectRedBrick();
          else if (event.effectiveTypeCode === 'h' && progression.recordPickup(`${level.id}:${pickup.id}`)) playtest.healPlayer(1);
          audio?.play(event.effectiveTypeCode === 'h' ? 'PickupHeart' : event.effectiveTypeCode === 'b' ? 'PickupCoinB' : 'PickupCoin');
        }
      }
      pickups.markDrawn(pickup.id, false);
      const model = meshes.get(pickup.id); if (!model) continue;
      const draw = pickups.getDrawState(pickup.id); model.visible = draw.visible;
      model.position.fromArray(draw.position); model.rotation.set(draw.rotationX, draw.rotationY, 0); model.scale.setScalar(draw.scale);
    }
    if (playtest.report.active && ledger) progression.advancePlayTime(dt);
    if (ledger && performance.now() - lastSave > 5000) { lastSave = performance.now(); try { progression.save(); } catch (error) { onError(error); } }
    if (performance.now() - lastHUD > 500) { updateHUD(); lastHUD = performance.now(); }
    const center = { ...actor.collisionPosition }, before = previousCenter; previousCenter = center;
    if (before && mission && playtest.report.active && !player.dead) mission.updateActor({ previousCenter: before, currentCenter: center, playerActive: true });
  }
  el('steam-folder').addEventListener('change', event => load(event.target.files));
  el('play-hosted').addEventListener('click', () => loadHosted().catch(onError));
  el('episode').addEventListener('change', populateChapters); el('chapter').addEventListener('change', populateLevels);
  el('open-level').addEventListener('click', openLevel); el('shop-item').addEventListener('change', updateHUD);
  el('buy-item').addEventListener('click', () => { try { progression.purchase(el('shop-item').value); progression.save(); updateHUD(); } catch (error) { onError(error); } });
  el('save-game').addEventListener('click', () => { try { progression.save(); el('save-status').textContent = 'Saved on this browser.'; } catch (error) { onError(error); el('save-status').textContent = 'Save failed; progress was not persisted.'; } });
  el('bank-run').addEventListener('click', () => { try { progression.saveAndExit(); progression.save(); updateHUD(); } catch (error) { onError(error); } });
  el('start-run').addEventListener('click', async () => {
    try { const current = progression.snapshot().run; if (current && current.chapter !== selectedChapter.id) throw new Error('Bank the current chapter ledger before starting another.');
      if (!current) progression.startRun(selectedChapter.id, 'story'); progression.save();
      el('level').value = selectedChapter.firstGameplayLevelId;
      await openLevel({ storySession: true }); updateHUD();
    } catch (error) { onError(error); }
  });
  el('skip-cutscene').addEventListener('click', () => mission?.skipCutscene({ userInitiated: true }));
  return { load, loadHosted, openLevel, openHub, update, report, world, clear() { clearPickups(); mission = null; campaign = false; hubMode = false; }, runtimeEvents,
    get interactions() { return interactions; }, get obstacles() { return obstacles; }, get mission() { return mission; }, get enemyController() { return enemyController; }, get rewards() { return rewards; }, get projectiles() { return projectiles; },
    get blowups() { return blowups; }, get panels() { return panels; }, get party() { return party; }, get sceneAnimation() { return sceneAnimation; }, get assets() { return assets; }, get catalog() { return catalog; }, get progression() { return progression; }, get pickups() { return pickups; } };
}
